import { escapeHtml } from './util.js';

// ===== Package config schema (Luna/Playworks build-time overridable) =====
// Only the store URLs are surfaced here; the preload name/icon live in their own
// "Preload" section (below) since they're DOM elements, not packageConfig keys.
const PKG_SCHEMA = [
  { key: 'iosLink',     type: 'url', label: 'iOS Store URL',     placeholder: 'https://apps.apple.com/…' },
  { key: 'androidLink', type: 'url', label: 'Android Store URL', placeholder: 'https://play.google.com/…' },
];

// ===== Preload section (the install/preload screen shown before the playable) =====
// These map to DOM elements the Luna template emits, not to packageConfig:
//   <span id="parameter/preloader/name">…</span>  — game name
//   <img  id="asset/preloader/icon" src="data:…"> — app icon
// We persist their overrides as reserved keys inside the canvas `pkg` object (so
// no schema/API change) and apply them by patching those elements in applyOverrides.
const PRELOAD_SECTION_ID = '__preload__';
const PRELOAD_NAME_KEY = '__pawPreloadName';
const PRELOAD_ICON_KEY = '__pawPreloadIcon';
let preloadDefaults = {};   // { name?, icon? } parsed from the playable

// Top-level globals Luna/Playworks builds use for runtime overrides.
// Each container holds named subgroups; each subgroup holds [type, ...values] entries.
const OVERRIDE_GLOBALS = [
  { global: 'playgroundOverrides',        labelPrefix: '' },
  { global: 'postProcessesOverrides',     labelPrefix: 'Post-Process' },
  { global: 'playgroundAssetOverrides',   labelPrefix: 'Assets' },
  { global: 'playgroundFiltersOverrides', labelPrefix: 'Filters' },
];

// Friendlier display names for known subgroups
const GROUP_LABELS = {
  PlayworksConfigVariableManager: 'Variables',
  ssao: 'SSAO',
};

const editorEl = document.getElementById('editorPanel');
const sectionNavEl = document.getElementById('sectionNav');

// Synthetic id for the package config section so the nav can target it.
const PKG_SECTION_ID = '__pkg__';

// ===== State =====
let currentSource = '';
let pkgDefaults = {};
let pkgOverrides = {};
let sections = [];                       // [{ global, group, label, entries: [{name,type,values,title?,order?,min?,max?,step?}] }]
let twOverrides = new Map();             // "global.group.name" → { global, group, name, type, values }
let rebuildTimer = null;
let onRebuild = null;
let searchByGroup = {};                  // group label → search query (per-section search)
let runtimeMetadata = null;              // Map<varName, {title, section, order, min, max, step}>
let runtimeDefaultSection = null;        // fallback section name from class-level LunaPlaygroundSection
let selectedSectionId = null;            // currently selected section id (or PKG_SECTION_ID)
let searchQuery = '';                    // search within the current section

export function setRebuildHandler(fn) { onRebuild = fn; }

// ===== Save / load bridge =====
// Snapshot of the live edit state, for persisting into a canvas.
export function getEditorState() {
  return { source: currentSource, pkg: { ...pkgOverrides }, tw: serializeTw(twOverrides) };
}

export function hasOverrides() {
  return Object.keys(pkgOverrides).length > 0 || twOverrides.size > 0;
}

function serializeTw(map) {
  return Array.from(map.values()).map((e) => ({
    global: e.global, group: e.group, name: e.name, type: e.type, values: [...e.values],
  }));
}

function deserializeTw(arr) {
  const m = new Map();
  (arr || []).forEach((e) => {
    m.set(tweakableKey(e.global, e.group, e.name), {
      global: e.global, group: e.group, name: e.name, type: e.type, values: [...e.values],
    });
  });
  return m;
}

// Apply a canvas's saved overrides on top of a freshly-activated editor.
// Call AFTER activateEditor(source): the override Maps are keyed by
// global.group.name, so they survive the later enhanceFromRuntime() re-render.
export function loadOverridesIntoEditor({ pkg, tw }) {
  pkgOverrides = pkg ? { ...pkg } : {};
  twOverrides = deserializeTw(tw);
  if (hasAnyDefaults()) render(); else renderEmpty();
  scheduleRebuild();
}

// ===== Runtime metadata (from Bridge.NET reflection in iframe) =====
// Reads UnityEngine.LunaPlaygroundFieldAttribute / RangeAttribute / LunaPlaygroundFieldStepAttribute
// off each member of window.PlayworksConfigVariableManager.$getMetadata().m[].at[]
export function enhanceFromRuntime(win) {
  // Already processed for this source — re-running would call render() and
  // destroy in-flight inputs, resetting their values to the parsed defaults.
  if (runtimeMetadata) return;
  try {
    const klass = win.PlayworksConfigVariableManager;
    if (!klass || typeof klass.$getMetadata !== 'function') return;
    const meta = klass.$getMetadata();
    if (!meta || !Array.isArray(meta.m)) return;

    // Class-level LunaPlaygroundSection — fallback section when a field
    // doesn't specify its own (per Luna docs).
    const classAttrs = Array.isArray(meta.at) ? meta.at : [];
    for (const attr of classAttrs) {
      const fn = (attr.constructor && attr.constructor.$$fullname) || '';
      if (/PlaygroundSectionAttribute$/.test(fn)) {
        runtimeDefaultSection = attr.SectionName ?? attr.sectionName
                             ?? attr.Name ?? attr.name ?? null;
        break;
      }
    }

    const unknownAttrs = new Set();
    const out = new Map();
    for (const mem of meta.m) {
      if (!mem || !Array.isArray(mem.at) || mem.at.length === 0) continue;
      const info = { name: mem.n };
      for (const attr of mem.at) {
        if (!attr) continue;
        const fn = (attr.constructor && attr.constructor.$$fullname) || '';
        const matched = matchAttribute(fn, attr, info);
        if (!matched && fn) unknownAttrs.add(fn);
      }
      // Enum option labels live on the runtime type's $names array
      // (e.g. EUpgradeOption → ["SummonHero_Akari", …]). For enum[] fields the
      // names live on the array's element type.
      const opts = enumNamesFromType(win, mem.rt);
      if (opts) info.enumOptions = opts;
      if (info.section || info.enumOptions) out.set(mem.n, info);
    }
    if (unknownAttrs.size) {
      console.info('[editor] unrecognized Playground attributes (extend matchAttribute):',
        Array.from(unknownAttrs));
    }
    if (out.size === 0) return;
    runtimeMetadata = out;
    if (currentSource) {
      sections = buildSections(currentSource);
      render();
    }
  } catch (e) {
    console.warn('[editor] runtime metadata read failed:', e);
  }
}

// Map a Bridge.NET attribute (by its $$fullname) onto our `info` object.
// Reference: https://docs.lunalabs.io/docs/playable/playable-setup/configure-game-variables
// Uses substring matching so minified/renamed variants still hit, and tries
// both PascalCase and camelCase property names so different Luna/Playworks
// versions work without code changes. Returns true if recognized.
function matchAttribute(fn, attr, info) {
  // LunaPlaygroundField  +  LunaPlaygroundAsset — both expose (FieldTitle,
  // FieldOrder, FieldSection) via the same shape per Luna docs.
  if (/Playground(Field|Asset)Attribute$/.test(fn)) {
    if (attr.FieldTitle   != null) info.title   = attr.FieldTitle;
    if (attr.FieldSection != null) info.section = attr.FieldSection;
    if (attr.FieldOrder   != null) info.order   = attr.FieldOrder;
    return true;
  }
  // Unity's Range — slider bounds for int/float
  if (/RangeAttribute$/.test(fn)) {
    const min = attr.min ?? attr.Min;
    const max = attr.max ?? attr.Max;
    if (min != null) info.min = min;
    if (max != null) info.max = max;
    return true;
  }
  // LunaPlaygroundFieldStep — step size for sliders (paired with Range)
  if (/PlaygroundFieldStepAttribute$/.test(fn)) {
    const step = attr.Step ?? attr.step;
    if (step != null) info.step = step;
    return true;
  }
  // LunaPlaygroundFieldArrayLength — min/max items for variable-length arrays
  if (/PlaygroundFieldArrayLengthAttribute$/.test(fn)) {
    const min = attr.MinLength ?? attr.minLength;
    const max = attr.MaxLength ?? attr.maxLength;
    if (min != null) info.minLength = min;
    if (max != null) info.maxLength = max;
    return true;
  }
  return false;
}

// Resolve a runtime field type to its enum option names (index → label).
// Bridge.NET enum types expose a `$names` array; for enum[] fields the element
// type carries it. Returns a string[] (value index = position) or null.
function enumNamesFromType(win, rt) {
  if (!rt) return null;
  try {
    let base = rt;
    if (rt.$isArray) {
      base = arrayElementType(win, rt);
    }
    if (base && Array.isArray(base.$names) && base.$names.length) {
      return base.$names.slice();
    }
  } catch { /* runtime shape varies — fall back to numeric */ }
  return null;
}

// Best-effort resolution of an array type's element type across Bridge versions.
function arrayElementType(win, rt) {
  const B = win.Bridge;
  try { if (rt.$elementType) return rt.$elementType; } catch { /* */ }
  try { if (B?.Reflection?.getElementType) { const e = B.Reflection.getElementType(rt); if (e) return e; } } catch { /* */ }
  try {
    const nm = (rt.$$name || '').replace(/\[\]$/, '');
    const all = B?.Reflection?.getTypes ? B.Reflection.getTypes() : [];
    for (const t of all) { if (t && t.$$name === nm) return t; }
  } catch { /* */ }
  return null;
}

// ===== Parsers =====
export function parsePackageConfig(text) {
  const block = text.match(/packageConfig\s*:\s*Object\.assign\s*\(\s*\{([\s\S]*?)\}\s*,\s*window\.LUNA_PLAYGROUND_PACKAGE_CONFIG/);
  if (!block) return null;
  const body = block[1];
  const out = {};
  const str = (k) => {
    const m = body.match(new RegExp(`${k}\\s*:\\s*"((?:[^"\\\\]|\\\\.)*)"`));
    return m ? m[1] : undefined;
  };
  const arr = (k) => {
    const m = body.match(new RegExp(`${k}\\s*:\\s*\\[([^\\]]*)\\]`));
    if (!m) return undefined;
    const items = m[1].match(/"((?:[^"\\]|\\.)*)"/g) || [];
    return items.map(s => s.slice(1, -1));
  };
  out.applicationName = str('applicationName');
  out.iosLink = str('iosLink');
  out.androidLink = str('androidLink');
  out.orientation = str('orientation');
  out.languages = arr('languages');
  const found = Object.values(out).some(v => v !== undefined);
  return found ? out : null;
}

// Parse the preload screen's game name + app icon from the Luna template DOM.
export function parsePreload(text) {
  const out = {};
  const nameM = text.match(/id="parameter\/preloader\/name"[^>]*>([\s\S]*?)<\/span>/i);
  if (nameM) out.name = nameM[1].trim();
  // The icon hook is normally an <img>, but the Preload tab may bake it into a
  // <video> for short clips — match either so a re-opened build still parses.
  const iconTag = text.match(/<(?:img|video)[^>]*id="asset\/preloader\/icon"[^>]*>/i);
  if (iconTag) {
    const srcM = iconTag[0].match(/\ssrc="([^"]*)"/i);
    if (srcM) out.icon = srcM[1];
  }
  return out;
}

// Patch the preload screen's name/icon elements in-place, returning new HTML.
// `name` is escaped as text; `icon` is a URL/data-URL written straight into src.
// Either field may be omitted to leave it untouched. Shared by the editor's
// preload overrides (applyOverrides) and the standalone Preload tab.
export function patchPreload(text, { name, icon } = {}) {
  let out = text;
  if (name !== undefined) {
    out = out.replace(/(id="parameter\/preloader\/name"[^>]*>)[\s\S]*?(<\/span>)/i,
      (m, a, b) => a + escapeHtml(name) + b);
  }
  if (icon !== undefined) {
    out = out.replace(/<img[^>]*id="asset\/preloader\/icon"[^>]*>/i, (tag) =>
      /\ssrc="/i.test(tag) ? tag.replace(/\ssrc="[^"]*"/i, ` src="${icon}"`)
                           : tag.replace(/<img/i, `<img src="${icon}"`));
  }
  return out;
}

// Extract a {...} substring starting at-or-after anchorIdx, returning {open, end, body}.
function extractBraced(text, anchorIdx) {
  const open = text.indexOf('{', anchorIdx);
  if (open < 0) return null;
  let depth = 0;
  for (let i = open; i < text.length; i++) {
    if (text[i] === '{') depth++;
    else if (text[i] === '}') {
      depth--;
      if (depth === 0) return { open, end: i, body: text.slice(open + 1, i) };
    }
  }
  return null;
}

// Walk a {…} body and yield top-level "Name": {…} subgroups.
function* iterSubgroups(body) {
  let i = 0;
  while (i < body.length) {
    const m = body.slice(i).match(/^\s*"([^"]+)"\s*:\s*\{/);
    if (!m) return;
    const start = i + m[0].length - 1; // position of opening brace within body
    let depth = 1;
    let j = start + 1;
    while (j < body.length && depth > 0) {
      if (body[j] === '{') depth++;
      else if (body[j] === '}') depth--;
      j++;
    }
    yield { name: m[1], body: body.slice(start + 1, j - 1) };
    i = j;
    while (i < body.length && /[\s,]/.test(body[i])) i++;
  }
}

function parseEntries(groupBody) {
  const re = /"([^"\\]+)"\s*:\s*\[\s*"([^"]+)"((?:\s*,\s*[^,\]]+)*)\s*\]/g;
  const out = [];
  let m;
  while ((m = re.exec(groupBody)) !== null) {
    const tail = m[3].split(',').map(s => s.trim()).filter(Boolean);
    const values = tail.map(v => {
      if (v === 'true') return true;
      if (v === 'false') return false;
      const n = Number(v);
      return Number.isNaN(n) ? v : n;
    });
    out.push({ name: m[1], type: m[2], values });
  }
  return out;
}

export function parseSections(text) {
  const result = [];
  for (const { global, labelPrefix } of OVERRIDE_GLOBALS) {
    const idx = text.indexOf(`window.${global} = `);
    if (idx < 0) continue;
    const extracted = extractBraced(text, idx);
    if (!extracted) continue;
    for (const sub of iterSubgroups(extracted.body)) {
      const entries = parseEntries(sub.body);
      if (entries.length === 0) continue;
      const groupLabel = GROUP_LABELS[sub.name] || sub.name;
      const label = labelPrefix ? `${labelPrefix} · ${groupLabel}` : groupLabel;
      result.push({ global, group: sub.name, label, entries });
    }
  }
  return result;
}

// Merge runtime-derived field metadata (title / min / max / step / array
// length / section) into a parsed entry. Range/Step/ArrayLength attributes
// apply to fields in any group, not just PlayworksConfigVariableManager.
function enrichEntry(entry) {
  if (!runtimeMetadata) return entry;
  const meta = runtimeMetadata.get(entry.name);
  if (!meta) return entry;
  const enriched = { ...entry };
  if (meta.title  != null) enriched.title = meta.title;
  if (typeof meta.order === 'number') enriched.order = meta.order;
  if (meta.min    != null) enriched.min  = meta.min;
  if (meta.max    != null) enriched.max  = meta.max;
  if (meta.step   != null) enriched.step = meta.step;
  if (meta.minLength != null) enriched.minLength = meta.minLength;
  if (meta.maxLength != null) enriched.maxLength = meta.maxLength;
  if (Array.isArray(meta.enumOptions)) enriched.options = meta.enumOptions;
  // Field-level section wins; class-level LunaPlaygroundSection is fallback.
  if (meta.section) enriched.section = meta.section;
  else if (runtimeDefaultSection) enriched.section = runtimeDefaultSection;
  return enriched;
}

// Build the final section list — parse the raw structure, then if runtime metadata
// is available, split PlayworksConfigVariableManager into multiple sections by FieldSection.
function buildSections(text) {
  const base = parseSections(text);
  if (!runtimeMetadata || runtimeMetadata.size === 0) return base;

  const out = [];
  for (const sec of base) {
    if (sec.global === 'playgroundOverrides' && sec.group === 'PlayworksConfigVariableManager') {
      // Re-group entries by FieldSection
      const groups = new Map();
      const unsectioned = [];
      sec.entries.forEach(entry => {
        const enriched = enrichEntry(entry);
        if (!enriched.section) { unsectioned.push(enriched); return; }
        if (!groups.has(enriched.section)) groups.set(enriched.section, []);
        groups.get(enriched.section).push(enriched);
      });
      // Sort entries within each section by FieldOrder
      groups.forEach((es) => es.sort((a, b) => (a.order ?? 999) - (b.order ?? 999)));
      // Emit each as its own section
      groups.forEach((entries, label) => {
        out.push({ global: sec.global, group: sec.group, label, entries });
      });
      // Any unsectioned vars fall into an "Other" bucket
      if (unsectioned.length) {
        out.push({ global: sec.global, group: sec.group, label: 'Other', entries: unsectioned });
      }
    } else {
      // Other override groups (post-process, asset, filter) still get
      // min/max/step enrichment so sliders + step values work the same way.
      out.push({ ...sec, entries: sec.entries.map(enrichEntry) });
    }
  }
  return out;
}

// ===== Reconcile overrides against a new base build =====
// When a creative's base HTML is re-uploaded, each canvas's saved overrides are
// reconciled to the new field schema: removed fields are dropped, type-changed
// fields are converted when possible (else reset to the new default), and
// unchanged fields keep their value. New fields appear from the base (no override).
// `twArray` is the serialized tw form ([{global,group,name,type,values}]).
export function reconcileOverrides(newSource, pkg, twArray) {
  const newPkg = parsePackageConfig(newSource) || {};
  const fieldType = new Map();          // "global.group.name" -> type
  parseSections(newSource).forEach((sec) => {
    sec.entries.forEach((e) => fieldType.set(`${sec.global}.${sec.group}.${e.name}`, e.type));
  });

  const outPkg = {};
  Object.keys(pkg || {}).forEach((k) => {
    if (newPkg[k] !== undefined) outPkg[k] = pkg[k];   // pkg field still present
  });
  // Preserve preload overrides if the new base still has the preload elements.
  const newPreload = parsePreload(newSource) || {};
  if (pkg && pkg[PRELOAD_NAME_KEY] !== undefined && newPreload.name !== undefined) outPkg[PRELOAD_NAME_KEY] = pkg[PRELOAD_NAME_KEY];
  if (pkg && pkg[PRELOAD_ICON_KEY] !== undefined && newPreload.icon !== undefined) outPkg[PRELOAD_ICON_KEY] = pkg[PRELOAD_ICON_KEY];

  const outTw = [];
  (twArray || []).forEach((e) => {
    const nt = fieldType.get(`${e.global}.${e.group}.${e.name}`);
    if (!nt) return;                                    // field removed → drop
    if (nt === e.type) { outTw.push(e); return; }       // unchanged → keep value
    const conv = convertValue(e.values, e.type, nt);
    if (conv) outTw.push({ ...e, type: nt, values: conv });  // converted
    // else: not convertible → drop (canvas falls back to the new base default)
  });

  return { pkg: outPkg, tw: outTw };
}

// Convert override values across numeric-ish types (int/float/enum/boolean);
// returns null when conversion doesn't make sense (caller then resets the field).
function convertValue(values, from, to) {
  const numeric = new Set(['int', 'float', 'enum', 'boolean']);
  if (!numeric.has(from) || !numeric.has(to)) return null;
  const out = values.map((v) => {
    let n = typeof v === 'boolean' ? (v ? 1 : 0) : Number(v);
    if (Number.isNaN(n)) return NaN;
    if (to === 'int' || to === 'enum') n = Math.round(n);
    if (to === 'boolean') n = n ? 1 : 0;
    return n;
  });
  return out.some((n) => Number.isNaN(n)) ? null : out;
}

// ===== Injection =====
export function applyOverrides(text, pkg, twMap) {
  let out = text;

  // Split the preload (DOM) overrides out of the package-config object.
  let realPkg = pkg, preloadName, preloadIcon;
  if (pkg && (PRELOAD_NAME_KEY in pkg || PRELOAD_ICON_KEY in pkg)) {
    realPkg = { ...pkg };
    if (PRELOAD_NAME_KEY in realPkg) { preloadName = realPkg[PRELOAD_NAME_KEY]; delete realPkg[PRELOAD_NAME_KEY]; }
    if (PRELOAD_ICON_KEY in realPkg) { preloadIcon = realPkg[PRELOAD_ICON_KEY]; delete realPkg[PRELOAD_ICON_KEY]; }
  }

  // Preload screen — patch the template's name/icon elements directly (flash-free).
  out = patchPreload(out, { name: preloadName, icon: preloadIcon });

  // Package config — inject before $environment script
  if (realPkg && Object.keys(realPkg).length) {
    const payload = JSON.stringify(realPkg);
    const inject = `<script data-paw-pkg>window.LUNA_PLAYGROUND_PACKAGE_CONFIG=Object.assign(window.LUNA_PLAYGROUND_PACKAGE_CONFIG||{},${payload});</script>`;
    const re = /<script\b[^>]*>(?=[^<]*window\.\$environment\s*=)/;
    if (re.test(out)) out = out.replace(re, inject + '$&');
    else out = out.replace(/<head\b[^>]*>/i, m => m + inject);
  }

  // Tweakable / post-process / asset / filter overrides — group by global
  if (twMap && twMap.size) {
    const byGlobal = new Map();
    twMap.forEach((entry) => {
      const arr = byGlobal.get(entry.global) || [];
      arr.push(entry);
      byGlobal.set(entry.global, arr);
    });

    const blocks = [];
    byGlobal.forEach((entries, global) => {
      const lines = [`if(!window.${global})window.${global}={};`];
      const groups = new Map();
      entries.forEach(e => {
        const a = groups.get(e.group) || [];
        a.push(e);
        groups.set(e.group, a);
      });
      groups.forEach((es, group) => {
        const accessor = `window.${global}[${JSON.stringify(group)}]`;
        lines.push(`${accessor}=${accessor}||{};`);
        es.forEach(e => {
          const arr = JSON.stringify([e.type, ...e.values]);
          lines.push(`${accessor}[${JSON.stringify(e.name)}]=${arr};`);
        });
      });
      blocks.push(lines.join(''));
    });

    const script = `<script data-paw-tw>(function(){${blocks.join('')}})();</script>`;

    // Inject after the script that defines window.playgroundOverrides (which also
    // defines the other override containers in the same tag).
    const opIdx = out.indexOf('window.playgroundOverrides = ');
    if (opIdx >= 0) {
      const closeIdx = out.indexOf('</script>', opIdx);
      if (closeIdx >= 0) {
        const after = closeIdx + '</script>'.length;
        out = out.slice(0, after) + script + out.slice(after);
      }
    }
  }

  return out;
}

// ===== Activation =====
export function activateEditor(text) {
  currentSource = text;
  pkgOverrides = {};
  twOverrides = new Map();
  searchByGroup = {};
  searchQuery = '';
  selectedSectionId = null;
  runtimeMetadata = null; // reset — preview.js will call enhanceFromRuntime once iframe boots
  runtimeDefaultSection = null;
  pkgDefaults = parsePackageConfig(text) || {};
  preloadDefaults = parsePreload(text) || {};
  // Seed pkgDefaults with the preload reserved keys so reset-to-default works.
  if (preloadDefaults.name !== undefined) pkgDefaults[PRELOAD_NAME_KEY] = preloadDefaults.name;
  if (preloadDefaults.icon !== undefined) pkgDefaults[PRELOAD_ICON_KEY] = preloadDefaults.icon;
  sections = buildSections(text);

  if (!hasAnyDefaults()) {
    renderEmpty();
    return;
  }
  render();
}

export function deactivateEditor() {
  currentSource = '';
  pkgDefaults = {};
  preloadDefaults = {};
  pkgOverrides = {};
  sections = [];
  twOverrides = new Map();
  searchByGroup = {};
  runtimeMetadata = null;
  runtimeDefaultSection = null;
  renderPlaceholder();
}

function pkgFieldCount() {
  return PKG_SCHEMA.filter(s => pkgDefaults[s.key] !== undefined).length;
}
function preloadFieldCount() {
  return (pkgDefaults[PRELOAD_NAME_KEY] !== undefined ? 1 : 0)
       + (pkgDefaults[PRELOAD_ICON_KEY] !== undefined ? 1 : 0);
}

function hasAnyDefaults() {
  return pkgFieldCount() > 0 || preloadFieldCount() > 0 || sections.length > 0;
}

function tweakableKey(global, group, name) {
  return `${global}.${group}.${name}`;
}

// ===== Rendering =====
function renderPlaceholder() {
  sectionNavEl.innerHTML = '';
  editorEl.innerHTML = `
    <div class="panel-placeholder">
      <div class="ph-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>
      </div>
      <h3>Editor</h3>
      <p>파일을 로드하면 다이나믹 변수가 여기에 나타납니다.</p>
    </div>`;
}

function renderEmpty() {
  sectionNavEl.innerHTML = '';
  editorEl.innerHTML = `
    <div class="panel-placeholder">
      <div class="ph-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
      </div>
      <h3>다이나믹 변수 없음</h3>
      <p>이 HTML에서 Luna/Playworks 다이나믹 변수 정의를 찾지 못했습니다.</p>
    </div>`;
}

// Build the navigation list: package config + each tweakable section.
function getNavList() {
  const list = [];
  const pkgCount = pkgFieldCount();
  if (pkgCount > 0) {
    list.push({ id: PKG_SECTION_ID, label: 'Package Config', count: pkgCount, kind: 'pkg' });
  }
  const preloadCount = preloadFieldCount();
  if (preloadCount > 0) {
    list.push({ id: PRELOAD_SECTION_ID, label: 'Preload', count: preloadCount, kind: 'preload' });
  }
  sections.forEach((sec) => {
    list.push({
      id: sectionId(sec),
      label: sec.label,
      count: sec.entries.length,
      kind: 'tw',
      section: sec,
    });
  });
  return list;
}

function sectionId(sec) {
  return `${sec.global}__${sec.group}__${sec.label}`;
}

function sectionHasOverride(item) {
  if (item.kind === 'pkg') return PKG_SCHEMA.some(s => Object.prototype.hasOwnProperty.call(pkgOverrides, s.key));
  if (item.kind === 'preload') return Object.prototype.hasOwnProperty.call(pkgOverrides, PRELOAD_NAME_KEY)
                                  || Object.prototype.hasOwnProperty.call(pkgOverrides, PRELOAD_ICON_KEY);
  return item.section.entries.some(e => twOverrides.has(tweakableKey(item.section.global, item.section.group, e.name)));
}

function render() {
  const list = getNavList();
  if (list.length === 0) { renderEmpty(); return; }
  if (!selectedSectionId || !list.find(s => s.id === selectedSectionId)) {
    selectedSectionId = list[0].id;
  }
  renderNav(list);
  renderBody(list);
}

function renderNav(list) {
  const twCount = sections.reduce((s, sec) => s + sec.entries.length, 0);
  const totalCount = pkgFieldCount() + preloadFieldCount() + twCount;

  sectionNavEl.innerHTML = `
    <div class="nav-head">
      <div class="nav-title">Dynamic Variables</div>
      <div class="nav-sub">${totalCount} total · ${list.length} section${list.length === 1 ? '' : 's'}</div>
    </div>
    <div class="nav-list" id="navList"></div>
    <div class="nav-foot">
      <button class="editor-reset" id="editorReset" type="button">Reset all</button>
    </div>
  `;

  const listEl = document.getElementById('navList');
  list.forEach(item => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'nav-item' + (item.id === selectedSectionId ? ' active' : '') + (sectionHasOverride(item) ? ' has-overrides' : '');
    btn.dataset.sectionId = item.id;
    btn.innerHTML = `
      <span class="nav-item-label">${escapeHtml(item.label)}</span>
      <span class="nav-item-count">${item.count}</span>`;
    btn.addEventListener('click', () => {
      if (selectedSectionId === item.id) return;
      selectedSectionId = item.id;
      searchQuery = '';
      render();
    });
    listEl.appendChild(btn);
  });

  document.getElementById('editorReset').addEventListener('click', resetAll);
}

function renderBody(list) {
  const item = list.find(s => s.id === selectedSectionId) || list[0];
  const showSearch = item.kind === 'tw' && item.count > 6;
  editorEl.innerHTML = `
    <div class="editor-root">
      <div class="editor-head">
        <div>
          <div class="editor-title">${escapeHtml(item.label)}</div>
          <div class="editor-sub">${item.count} field${item.count === 1 ? '' : 's'}${item.kind === 'tw' ? ' · ' + escapeHtml(item.section.group) : ''}</div>
        </div>
        ${showSearch ? `<input type="text" class="ed-search ed-search-lg" id="bodySearch" placeholder="Search…" value="${escapeHtml(searchQuery)}">` : ''}
      </div>
      <div class="editor-body" id="editorBody"></div>
    </div>`;

  const body = document.getElementById('editorBody');
  if (item.kind === 'pkg') {
    PKG_SCHEMA.forEach(spec => {
      if (pkgDefaults[spec.key] === undefined) return;
      body.appendChild(buildPkgField(spec));
    });
  } else if (item.kind === 'preload') {
    if (pkgDefaults[PRELOAD_NAME_KEY] !== undefined) body.appendChild(buildPreloadName());
    if (pkgDefaults[PRELOAD_ICON_KEY] !== undefined) body.appendChild(buildPreloadIcon());
  } else {
    item.section.entries.forEach(entry => body.appendChild(buildTwField(item.section, entry)));
  }

  if (showSearch) {
    const search = document.getElementById('bodySearch');
    const applyFilter = () => {
      const q = searchQuery.toLowerCase();
      body.querySelectorAll('[data-tw]').forEach(el => {
        const name = el.dataset.tw.toLowerCase();
        const title = (el.querySelector('.ed-label')?.textContent || '').toLowerCase();
        el.classList.toggle('ed-hidden', q && !name.includes(q) && !title.includes(q));
      });
    };
    search.addEventListener('input', (e) => { searchQuery = e.target.value; applyFilter(); });
    applyFilter();
  }
}

// ===== Package fields =====
function buildPkgField(spec) {
  const wrap = document.createElement('div');
  wrap.className = 'editor-field';
  wrap.dataset.pkg = spec.key;
  // Prefer the user's override if one exists, so a re-render doesn't snap
  // the field back to the parsed source default.
  const value = Object.prototype.hasOwnProperty.call(pkgOverrides, spec.key)
    ? pkgOverrides[spec.key]
    : pkgDefaults[spec.key];

  let widget = '';
  if (spec.type === 'string' || spec.type === 'url') {
    widget = `<input type="${spec.type === 'url' ? 'url' : 'text'}" class="ed-input" value="${escapeHtml(value || '')}" placeholder="${escapeHtml(spec.placeholder || '')}">`;
  } else if (spec.type === 'enum') {
    widget = `<div class="ed-segmented">${
      spec.options.map(opt => `<button type="button" class="ed-seg${opt === value ? ' active' : ''}" data-value="${escapeHtml(opt)}">${escapeHtml(opt)}</button>`).join('')
    }</div>`;
  } else if (spec.type === 'tags') {
    const tags = Array.isArray(value) ? value : [];
    widget = `<div class="ed-tags"><div class="ed-tags-list">${tags.map((t,i) => tagChip(t,i)).join('')}</div><input type="text" class="ed-tags-input" placeholder="${escapeHtml(spec.placeholder || '')}"></div>`;
  }

  wrap.innerHTML = `<label class="ed-label">${escapeHtml(spec.label)}</label>${widget}<div class="ed-key">${escapeHtml(spec.key)}</div>`;
  attachPkgListeners(wrap, spec);
  return wrap;
}

function tagChip(text, idx) {
  return `<span class="ed-tag" data-idx="${idx}">${escapeHtml(text)}<button type="button" class="ed-tag-x" aria-label="remove">×</button></span>`;
}

function attachPkgListeners(wrap, spec) {
  if (spec.type === 'string' || spec.type === 'url') {
    const inp = wrap.querySelector('input');
    inp.addEventListener('input', () => { setPkgOverride(spec.key, inp.value); scheduleRebuild(); });
  } else if (spec.type === 'enum') {
    wrap.querySelectorAll('.ed-seg').forEach(btn => {
      btn.addEventListener('click', () => {
        wrap.querySelectorAll('.ed-seg').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        setPkgOverride(spec.key, btn.dataset.value);
        scheduleRebuild();
      });
    });
  } else if (spec.type === 'tags') {
    const listEl = wrap.querySelector('.ed-tags-list');
    const inp = wrap.querySelector('.ed-tags-input');
    const getTags = () => Array.from(listEl.querySelectorAll('.ed-tag')).map(el => el.firstChild.textContent);
    const rebuildList = () => {
      const tags = getTags();
      listEl.innerHTML = tags.map((t,i) => tagChip(t,i)).join('');
      bindRemove();
      setPkgOverride(spec.key, tags);
      scheduleRebuild();
    };
    const bindRemove = () => listEl.querySelectorAll('.ed-tag-x').forEach(x => x.addEventListener('click', e => {
      e.target.closest('.ed-tag').remove();
      rebuildList();
    }));
    bindRemove();
    inp.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        const v = inp.value.trim().replace(/,$/, '');
        if (!v) return;
        const tags = getTags(); tags.push(v);
        listEl.innerHTML = tags.map((t,i) => tagChip(t,i)).join('');
        inp.value = '';
        bindRemove();
        setPkgOverride(spec.key, tags);
        scheduleRebuild();
      } else if (e.key === 'Backspace' && !inp.value) {
        const last = listEl.querySelector('.ed-tag:last-child');
        if (last) { last.remove(); rebuildList(); }
      }
    });
  }
}

function setPkgOverride(key, value) {
  const def = pkgDefaults[key];
  const same = Array.isArray(def)
    ? Array.isArray(value) && def.length === value.length && def.every((v,i) => v === value[i])
    : def === value;
  if (same) delete pkgOverrides[key];
  else pkgOverrides[key] = value;
  refreshNavDots();
}

function refreshNavDots() {
  if (!sectionNavEl.querySelector('.nav-list')) return;
  const items = getNavList();
  sectionNavEl.querySelectorAll('.nav-item').forEach(btn => {
    const id = btn.dataset.sectionId;
    const item = items.find(it => it.id === id);
    if (!item) return;
    btn.classList.toggle('has-overrides', sectionHasOverride(item));
  });
}

// ===== Preload fields =====
function preloadValue(key) {
  return Object.prototype.hasOwnProperty.call(pkgOverrides, key) ? pkgOverrides[key] : pkgDefaults[key];
}

function buildPreloadName() {
  const wrap = document.createElement('div');
  wrap.className = 'editor-field';
  const value = preloadValue(PRELOAD_NAME_KEY) || '';
  wrap.innerHTML = `<label class="ed-label">게임 이름</label>
    <input type="text" class="ed-input" value="${escapeHtml(value)}" placeholder="프리로드 화면에 표시될 이름">
    <div class="ed-key">parameter/preloader/name</div>`;
  const inp = wrap.querySelector('input');
  inp.addEventListener('input', () => { setPkgOverride(PRELOAD_NAME_KEY, inp.value); scheduleRebuild(); });
  return wrap;
}

function buildPreloadIcon() {
  const wrap = document.createElement('div');
  wrap.className = 'editor-field';
  const value = preloadValue(PRELOAD_ICON_KEY) || '';
  const overridden = Object.prototype.hasOwnProperty.call(pkgOverrides, PRELOAD_ICON_KEY);
  wrap.innerHTML = `<label class="ed-label">앱 아이콘</label>
    <div class="ed-image">
      <img class="ed-image-preview" alt="app icon" src="${escapeHtml(value)}">
      <div class="ed-image-actions">
        <button type="button" class="ed-image-pick">이미지 선택</button>
        <button type="button" class="ed-image-reset"${overridden ? '' : ' hidden'}>기본값</button>
      </div>
      <input type="file" accept="image/*" hidden>
    </div>
    <div class="ed-key">asset/preloader/icon</div>`;
  const preview = wrap.querySelector('.ed-image-preview');
  const fileInp = wrap.querySelector('input[type=file]');
  const resetBtn = wrap.querySelector('.ed-image-reset');
  wrap.querySelector('.ed-image-pick').addEventListener('click', () => fileInp.click());
  fileInp.addEventListener('change', () => {
    const file = fileInp.files && fileInp.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result);
      setPkgOverride(PRELOAD_ICON_KEY, dataUrl);
      preview.src = dataUrl;
      resetBtn.hidden = false;
      scheduleRebuild();
    };
    reader.readAsDataURL(file);
  });
  resetBtn.addEventListener('click', () => {
    setPkgOverride(PRELOAD_ICON_KEY, pkgDefaults[PRELOAD_ICON_KEY]);  // back to default → override removed
    preview.src = pkgDefaults[PRELOAD_ICON_KEY] || '';
    resetBtn.hidden = true;
    scheduleRebuild();
  });
  return wrap;
}

// ===== Tweakable fields =====
function buildTwField(section, entry) {
  const wrap = document.createElement('div');
  wrap.className = 'editor-field tw';
  wrap.dataset.tw = entry.name;
  wrap.dataset.sec = String(sections.indexOf(section));
  // If the user has already overridden this field, render the widget with
  // their value — not the source default — so a re-render (e.g. after the
  // iframe reloads and surfaces runtime metadata) doesn't blow away input.
  const override = twOverrides.get(tweakableKey(section.global, section.group, entry.name));
  const widgetEntry = override ? { ...entry, values: override.values } : entry;
  const widget = buildTwWidget(widgetEntry);
  const displayLabel = entry.title || entry.name;
  const subKey = entry.title ? `<div class="ed-key">${escapeHtml(entry.name)}</div>` : '';
  wrap.innerHTML = `
    <label class="ed-label">${escapeHtml(displayLabel)} <span class="ed-type">${escapeHtml(entry.type)}</span></label>
    ${widget.html}
    ${subKey}`;
  widget.attach(wrap, section, entry);
  return wrap;
}

function buildTwWidget(entry) {
  // Enum (and enum arrays) with known option labels → dropdown(s). Falls through
  // to the numeric widget below when labels aren't available (e.g. enums from a
  // manager whose runtime metadata we didn't read).
  const isEnumArray = entry.type === 'enum[]';
  if ((entry.type === 'enum' || isEnumArray) && Array.isArray(entry.options) && entry.options.length) {
    const opts = entry.options;
    const optionsHtml = (sel) => opts
      .map((o, i) => `<option value="${i}"${i === sel ? ' selected' : ''}>${escapeHtml(o)} · ${i}</option>`)
      .join('');

    // Scalar enum — single dropdown
    if (!isEnumArray) {
      const cur = Number(entry.values[0]) || 0;
      return {
        html: `<select class="ed-input ed-enum">${optionsHtml(cur)}</select>`,
        attach(wrap, section, e) {
          const sel = wrap.querySelector('select');
          sel.addEventListener('change', () => {
            setTwOverride(section, e, [parseInt(sel.value, 10)]);
            scheduleRebuild();
          });
        },
      };
    }

    // Enum array — one dropdown per index, stacked vertically, with add/remove.
    const rowHtml = (val, i) => `
      <div class="ed-enum-item" data-i="${i}">
        <span class="ed-enum-idx">#${i + 1}</span>
        <select class="ed-input ed-enum">${optionsHtml(Number(val) || 0)}</select>
        <button type="button" class="ed-enum-del" aria-label="삭제" title="삭제">×</button>
      </div>`;
    return {
      html: `
        <div class="ed-enum-list">
          ${entry.values.map((v, i) => rowHtml(v, i)).join('')}
          <button type="button" class="ed-enum-add">+ 후보 추가</button>
        </div>`,
      attach(wrap, section, e) {
        const listEl = wrap.querySelector('.ed-enum-list');
        const collect = () => Array.from(listEl.querySelectorAll('.ed-enum-item select'))
          .map((s) => parseInt(s.value, 10));
        const commit = () => { setTwOverride(section, e, collect()); scheduleRebuild(); };
        const rebuild = () => {
          const vals = collect();
          listEl.innerHTML = vals.map((v, i) => rowHtml(v, i)).join('')
            + '<button type="button" class="ed-enum-add">+ 후보 추가</button>';
          bind();
          commit();
        };
        function bind() {
          listEl.querySelectorAll('.ed-enum-item select').forEach((s) => s.addEventListener('change', commit));
          listEl.querySelectorAll('.ed-enum-del').forEach((b) => b.addEventListener('click', () => {
            b.closest('.ed-enum-item').remove();
            rebuild();
          }));
          const add = listEl.querySelector('.ed-enum-add');
          if (add) add.addEventListener('click', () => {
            const vals = collect(); vals.push(0);
            setTwOverride(section, e, vals);
            listEl.innerHTML = vals.map((v, i) => rowHtml(v, i)).join('')
              + '<button type="button" class="ed-enum-add">+ 후보 추가</button>';
            bind();
            scheduleRebuild();
          });
        }
        bind();
      },
    };
  }
  if (entry.type === 'int' || entry.type === 'float' || entry.type === 'enum') {
    const isFloat = entry.type === 'float';
    const step = entry.step != null ? String(entry.step) : (isFloat ? 'any' : '1');
    const hasRange = typeof entry.min === 'number' && typeof entry.max === 'number';
    const minAttr = hasRange ? `min="${entry.min}"` : '';
    const maxAttr = hasRange ? `max="${entry.max}"` : '';
    const cur = entry.values[0];
    // When a Range is provided, render as slider + number readout
    if (hasRange) {
      return {
        html: `
          <div class="ed-range-row">
            <input type="range" class="ed-range" step="${step}" ${minAttr} ${maxAttr} value="${cur}">
            <input type="number" class="ed-input ed-num ed-num-tight" step="${step}" ${minAttr} ${maxAttr} value="${cur}">
          </div>`,
        attach(wrap, section, e) {
          const range = wrap.querySelector('.ed-range');
          const num = wrap.querySelector('.ed-num');
          // Dragging the slider updates the override value + number readout
          // live (cheap), but only rebuilds the iframe on release ('change') —
          // avoids reloading multi-MB playables on every drag frame.
          const setVal = (raw, rebuild) => {
            if (raw === '' || raw === '-') return;
            const v = isFloat ? parseFloat(raw) : parseInt(raw, 10);
            if (Number.isNaN(v)) return;
            setTwOverride(section, e, [v]);
            if (rebuild) scheduleRebuild();
          };
          range.addEventListener('input', () => { num.value = range.value; setVal(range.value, false); });
          range.addEventListener('change', () => { setVal(range.value, true); });
          num.addEventListener('input', () => { range.value = num.value; setVal(num.value, true); });
        }
      };
    }
    // Resolve a numeric step for the ± buttons. Native stepUp()/stepDown()
    // throws on step="any" (which we use for floats without an explicit step),
    // so we always do the math ourselves.
    const stepNum = entry.step != null ? Number(entry.step) : (isFloat ? 0.1 : 1);
    const decimals = isFloat ? ((String(stepNum).split('.')[1] || '').length || 1) : 0;
    return {
      html: `
        <div class="ed-stepper">
          <button type="button" class="ed-stepper-btn" data-dir="-1" aria-label="감소">−</button>
          <input type="number" step="${step}" ${minAttr} ${maxAttr} class="ed-input ed-num" value="${cur}">
          <button type="button" class="ed-stepper-btn" data-dir="1" aria-label="증가">+</button>
        </div>`,
      attach(wrap, section, e) {
        const inp = wrap.querySelector('input');
        const commit = (v) => { setTwOverride(section, e, [v]); scheduleRebuild(); };
        inp.addEventListener('input', () => {
          const raw = inp.value;
          if (raw === '' || raw === '-') return;
          const v = isFloat ? parseFloat(raw) : parseInt(raw, 10);
          if (Number.isNaN(v)) return;
          commit(v);
        });
        wrap.querySelectorAll('.ed-stepper-btn').forEach(btn => {
          btn.addEventListener('click', () => {
            const dir = parseInt(btn.dataset.dir, 10);
            const cur = (isFloat ? parseFloat(inp.value) : parseInt(inp.value, 10)) || 0;
            let next = cur + dir * stepNum;
            if (typeof e.min === 'number') next = Math.max(e.min, next);
            if (typeof e.max === 'number') next = Math.min(e.max, next);
            // Avoid float-arithmetic drift (e.g. 0.1 + 0.2)
            if (isFloat) next = parseFloat(next.toFixed(decimals));
            inp.value = String(next);
            commit(next);
          });
        });
      }
    };
  }
  if (entry.type === 'boolean') {
    const checked = entry.values[0] ? 'checked' : '';
    return {
      html: `<label class="ed-toggle"><input type="checkbox" ${checked}><span class="ed-toggle-track"><span class="ed-toggle-thumb"></span></span></label>`,
      attach(wrap, section, e) {
        const inp = wrap.querySelector('input');
        inp.addEventListener('change', () => {
          setTwOverride(section, e, [inp.checked ? 1 : 0]);
          scheduleRebuild();
        });
      }
    };
  }
  if (entry.type === 'color') {
    const [r,g,b,a] = entry.values;
    const hex = '#' + [r,g,b].map(v => Math.round(Math.max(0,Math.min(1,v))*255).toString(16).padStart(2,'0')).join('');
    return {
      html: `
        <div class="ed-color-row">
          <input type="color" class="ed-color" value="${hex}">
          <span class="ed-color-hex">${hex.toUpperCase()}</span>
          <label class="ed-color-alpha">α<input type="number" min="0" max="1" step="0.01" value="${a}"></label>
        </div>`,
      attach(wrap, section, e) {
        const col = wrap.querySelector('.ed-color');
        const hexEl = wrap.querySelector('.ed-color-hex');
        const alpha = wrap.querySelector('.ed-color-alpha input');
        const sync = () => {
          const v = col.value;
          hexEl.textContent = v.toUpperCase();
          const rr = parseInt(v.slice(1,3),16)/255;
          const gg = parseInt(v.slice(3,5),16)/255;
          const bb = parseInt(v.slice(5,7),16)/255;
          const aa = parseFloat(alpha.value);
          setTwOverride(section, e, [rr, gg, bb, Number.isNaN(aa) ? a : aa]);
          scheduleRebuild();
        };
        col.addEventListener('input', sync);
        alpha.addEventListener('input', sync);
      }
    };
  }
  if (entry.type === 'int[]') {
    const csv = entry.values.join(', ');
    return {
      html: `<input type="text" class="ed-input ed-csv" value="${escapeHtml(csv)}" placeholder="100, 150, 200">`,
      attach(wrap, section, e) {
        const inp = wrap.querySelector('input');
        inp.addEventListener('input', () => {
          const parts = inp.value.split(',').map(s => s.trim()).filter(Boolean).map(s => parseInt(s, 10)).filter(n => !Number.isNaN(n));
          setTwOverride(section, e, parts);
          scheduleRebuild();
        });
      }
    };
  }
  // Fallback: JSON text
  return {
    html: `<input type="text" class="ed-input" value="${escapeHtml(JSON.stringify(entry.values))}">`,
    attach(wrap, section, e) {
      const inp = wrap.querySelector('input');
      inp.addEventListener('input', () => {
        try {
          const v = JSON.parse(inp.value);
          if (Array.isArray(v)) { setTwOverride(section, e, v); scheduleRebuild(); }
        } catch { /* ignore partial input */ }
      });
    }
  };
}

function setTwOverride(section, entry, newValues) {
  const same = entry.values.length === newValues.length && entry.values.every((v,i) => v === newValues[i]);
  const key = tweakableKey(section.global, section.group, entry.name);
  if (same) twOverrides.delete(key);
  else twOverrides.set(key, {
    global: section.global,
    group: section.group,
    name: entry.name,
    type: entry.type,
    values: newValues,
  });
  refreshNavDots();
}

// ===== Reset / rebuild =====
function resetAll() {
  pkgOverrides = {};
  twOverrides = new Map();
  render();
  scheduleRebuild();
}

function scheduleRebuild() {
  if (rebuildTimer) clearTimeout(rebuildTimer);
  rebuildTimer = setTimeout(() => {
    rebuildTimer = null;
    if (onRebuild) onRebuild({ pkg: pkgOverrides, tw: twOverrides });
  }, 320);
}

renderPlaceholder();
